import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Save, Eye, Upload, Image, Bold, Italic, Link, List, AlignLeft, AlignCenter, Code, Quote, Heading1, Heading2, Trash2, Edit3, Search, Filter, Calendar, Tag, User, Globe, FileText, Settings, X, Check, AlertCircle, Undo, Redo, Type, Palette, AlignJustify, Strikethrough, TableIcon, FileIcon, Download } from 'lucide-react';
import { apiRequest, apiRequestMultipart } from '../../utils/apiHelper';

const BlogAdminUpload = () => {
  const [activeTab, setActiveTab] = useState('list');
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [submitting, setSubmitting] = useState(false);
  const [selectedFormats, setSelectedFormats] = useState({
    bold: false,
    italic: false,
    underline: false,
    fontSize: '',
    fontFamily: '',
    textColor: '#000000',
    bgColor: '#ffffff',
    alignment: 'left'
  });
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [wordCount, setWordCount] = useState(0);
  const [charCount, setCharCount] = useState(0);
  const [lastSaved, setLastSaved] = useState(null);
  const [isAutoSaving, setIsAutoSaving] = useState(false);

  // API base URL - Update this to match your backend
  const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api/blogs`;
  const UPLOAD_API_URL = `${process.env.REACT_APP_API_BASE_URL}/api/siridharini/blog-uploads`;

  const [blogData, setBlogData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    featuredImageUrl: '',
    category: '',
    tags: [],
    status: 'DRAFT',
    author: 'Admin User',
    seoTitle: '',
    seoDescription: '',
    bgColor: '',
    iconName: 'FileText',
    displayOrder: 0,
    isActive: true,
    readTime: ''
  });
  
  const [images, setImages] = useState([]);
  const [pdfs, setPdfs] = useState([]);
  const [showImageManager, setShowImageManager] = useState(false);
  const [showPdfManager, setShowPdfManager] = useState(false);
  const [editorContent, setEditorContent] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [blogs, setBlogs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categories, setCategories] = useState([]);
  const [authors, setAuthors] = useState([]);
  
  const fileInputRef = useRef(null);
  const pdfInputRef = useRef(null);
  const featuredImageRef = useRef(null);
  const editorRef = useRef(null);
  const isFormattingRef = useRef(false);
  const autoSaveTimerRef = useRef(null);
  const colorPickerRef = useRef(null);
  const bgColorPickerRef = useRef(null);

  // Debounced content update
  const debouncedContentUpdate = useCallback(
    (() => {
      let timeoutId;
      return (content) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          if (!isFormattingRef.current) {
            setEditorContent(content);
          }
        }, 300);
      };
    })(),
    []
  );

  // Fix text direction in editor
  const fixTextDirection = useCallback(() => {
    if (editorRef.current) {
      const editor = editorRef.current;
      
      // Set main editor direction
      editor.style.direction = 'ltr';
      editor.style.textAlign = 'left';
      editor.style.unicodeBidi = 'normal';
      editor.setAttribute('dir', 'ltr');
      
      // Fix all child elements
      const allElements = editor.querySelectorAll('*');
      allElements.forEach(element => {
        element.style.direction = 'ltr';
        element.style.textAlign = 'left';
        element.style.unicodeBidi = 'normal';
        element.setAttribute('dir', 'ltr');
      });
    }
  }, []);

  // Save state for undo (moved outside formatText for reuse)
  const saveUndoState = useCallback(() => {
    if (!editorRef.current) return;
    const currentContent = editorRef.current.innerHTML;
    setUndoStack(prev => {
      const newStack = [...prev];
      // Only add if different from last state
      if (newStack.length === 0 || newStack[newStack.length - 1] !== currentContent) {
        newStack.push(currentContent);
        // Keep only last 50 states
        return newStack.slice(-50);
      }
      return prev;
    });
    setRedoStack([]);
  }, []);

  // Enhanced format text function with undo support
  const formatText = useCallback((command, value = null) => {
    if (!editorRef.current) return;

    isFormattingRef.current = true;
    
    // Save state for undo before making changes
    saveUndoState();
    
    // Save current selection
    const selection = window.getSelection();
    const range = selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
    
    // Focus editor
    editorRef.current.focus();
    
    // Restore selection if it existed
    if (range) {
      selection.removeAllRanges();
      selection.addRange(range);
    }

    // Execute command
    try {
      if (command === 'formatBlock') {
        document.execCommand(command, false, value);
      } else if (command === 'createLink') {
        const url = prompt('Enter URL:');
        if (url) {
          document.execCommand('createLink', false, url);
        }
      } else if (command === 'insertTable') {
        insertTable();
      } else if (command === 'foreColor') {
        document.execCommand('foreColor', false, value);
      } else if (command === 'hiliteColor') {
        document.execCommand('hiliteColor', false, value);
      } else if (command === 'fontName') {
        document.execCommand('fontName', false, value);
      } else {
        document.execCommand(command, false, value);
      }
    } catch (error) {
      console.error('Format command error:', error);
    }

    // Fix text direction after formatting
    setTimeout(() => {
      fixTextDirection();
      if (editorRef.current) {
        const newContent = editorRef.current.innerHTML;
        setEditorContent(newContent);
        updateWordCount(newContent);
      }
      isFormattingRef.current = false;
      detectFormattingAtCursor();
    }, 100);
  }, [fixTextDirection, saveUndoState]);

  // Detect formatting at cursor position
  const detectFormattingAtCursor = useCallback(() => {
    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    const formats = {
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      underline: document.queryCommandState('underline'),
      fontSize: document.queryCommandValue('fontSize'),
      fontFamily: document.queryCommandValue('fontName'),
      textColor: document.queryCommandValue('foreColor'),
      bgColor: document.queryCommandValue('hiliteColor'),
      alignment: getTextAlignment()
    };

    setSelectedFormats(formats);
  }, []);

  // Get text alignment
  const getTextAlignment = () => {
    if (document.queryCommandState('justifyLeft')) return 'left';
    if (document.queryCommandState('justifyCenter')) return 'center';
    if (document.queryCommandState('justifyRight')) return 'right';
    if (document.queryCommandState('justifyFull')) return 'justify';
    return 'left';
  };

  // Insert table
  const insertTable = () => {
    const rows = prompt('Number of rows:', '3');
    const cols = prompt('Number of columns:', '3');
    
    if (!rows || !cols) return;
    
    let tableHtml = '<table style="border-collapse: collapse; width: 100%; margin: 10px 0;">';
    for (let i = 0; i < parseInt(rows); i++) {
      tableHtml += '<tr>';
      for (let j = 0; j < parseInt(cols); j++) {
        tableHtml += '<td style="border: 1px solid #ddd; padding: 8px; min-width: 50px;">&nbsp;</td>';
      }
      tableHtml += '</tr>';
    }
    tableHtml += '</table><p><br></p>';
    
    document.execCommand('insertHTML', false, tableHtml);
  };

  // Undo functionality
  const handleUndo = useCallback(() => {
    if (!editorRef.current || undoStack.length <= 1) return;
    
    const currentContent = editorRef.current.innerHTML;
    const previousContent = undoStack[undoStack.length - 2]; // Get second to last
    
    // Move current state to redo stack
    setRedoStack(prev => [...prev, currentContent].slice(-50));
    // Remove last item from undo stack
    setUndoStack(prev => prev.slice(0, -1));
    
    // Apply previous content
    editorRef.current.innerHTML = previousContent;
    setEditorContent(previousContent);
    updateWordCount(previousContent);
    detectFormattingAtCursor();
  }, [undoStack, detectFormattingAtCursor]);

  // Redo functionality  
  const handleRedo = useCallback(() => {
    if (!editorRef.current || redoStack.length === 0) return;
    
    const nextContent = redoStack[redoStack.length - 1];
    const currentContent = editorRef.current.innerHTML;
    
    // Add current to undo stack
    setUndoStack(prev => [...prev, currentContent].slice(-50));
    // Remove from redo stack
    setRedoStack(prev => prev.slice(0, -1));
    
    // Apply next content
    editorRef.current.innerHTML = nextContent;
    setEditorContent(nextContent);
    updateWordCount(nextContent);
    detectFormattingAtCursor();
  }, [redoStack, detectFormattingAtCursor]);

  // Update word and character count
  const updateWordCount = (html) => {
    const text = html.replace(/<[^>]*>/g, '').trim();
    const words = text.split(/\s+/).filter(word => word.length > 0);
    setWordCount(words.length);
    setCharCount(text.length);
  };

  // Auto-save functionality
  const autoSaveDraft = useCallback(async () => {
    if (!blogData.title && !editorContent) return;
    
    setIsAutoSaving(true);
    
    try {
      const draftData = {
        ...blogData,
        content: editorContent,
        status: 'DRAFT',
        lastAutoSaved: new Date().toISOString()
      };
      
      // Save to localStorage as backup
      localStorage.setItem('blogDraft', JSON.stringify(draftData));
      
      // If editing existing blog, save to server
      if (editingId) {
        const response = await apiRequest(`${API_BASE_URL}/admin/${editingId}/auto-save`, {
          method: 'PATCH',
          body: JSON.stringify({
            content: editorContent,
            title: blogData.title,
            excerpt: blogData.excerpt
          })
        });
        
        if (response.ok) {
          setLastSaved(new Date());
        }
      }
    } catch (error) {
      console.error('Auto-save error:', error);
    } finally {
      setIsAutoSaving(false);
    }
  }, [blogData, editorContent, editingId]);

  // Debounced undo state save
  const debouncedSaveUndo = useCallback(
    (() => {
      let timeoutId;
      return () => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          saveUndoState();
        }, 500); // Save undo state after 500ms of no typing
      };
    })(),
    [saveUndoState]
  );

  // Handle editor content change
  const handleEditorInput = useCallback((e) => {
    const content = e.target.innerHTML;
    
    // Fix direction immediately
    fixTextDirection();
    
    // Update content with debounce
    debouncedContentUpdate(content);
    
    // Save undo state with debounce
    debouncedSaveUndo();
    
    // Update word count
    updateWordCount(content);
    
    // Detect formatting at cursor
    detectFormattingAtCursor();
    
    // Reset auto-save timer
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    autoSaveTimerRef.current = setTimeout(() => {
      autoSaveDraft();
    }, 5000); // Auto-save after 5 seconds of inactivity
  }, [fixTextDirection, debouncedContentUpdate, detectFormattingAtCursor, autoSaveDraft, debouncedSaveUndo]);

  // Handle editor events
  const handleEditorFocus = useCallback(() => {
    setTimeout(() => {
      fixTextDirection();
      detectFormattingAtCursor();
    }, 50);
  }, [fixTextDirection, detectFormattingAtCursor]);

  const handleEditorKeyDown = useCallback((e) => {
    // Detect OS
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    const modKey = isMac ? e.metaKey : e.ctrlKey;
    
    // Handle keyboard shortcuts with both Ctrl (Windows) and Cmd (Mac)
    if (modKey) {
      switch(e.key.toLowerCase()) {
        case 'b':
          e.preventDefault();
          formatText('bold');
          break;
        case 'i':
          e.preventDefault();
          formatText('italic');
          break;
        case 'u':
          e.preventDefault();
          formatText('underline');
          break;
        case 'z':
          e.preventDefault();
          if (e.shiftKey) {
            console.log('Redo triggered');
            handleRedo();
          } else {
            console.log('Undo triggered');
            handleUndo();
          }
          break;
        case 'y':
          e.preventDefault();
          console.log('Redo triggered (Ctrl+Y)');
          handleRedo();
          break;
        case 's':
          e.preventDefault();
          autoSaveDraft();
          setMessage({ type: 'success', text: 'Draft saved!' });
          break;
        case 'k':
          e.preventDefault();
          formatText('createLink');
          break;
        case 'e':
          e.preventDefault();
          formatText('justifyCenter');
          break;
        case 'l':
          e.preventDefault();
          formatText('justifyLeft');
          break;
        case 'r':
          e.preventDefault();
          formatText('justifyRight');
          break;
      }
    }
    
    // Save undo state before certain keys
    if (e.key === 'Enter' || e.key === 'Delete' || e.key === 'Backspace') {
      saveUndoState();
      // Handle Enter key to maintain direction
      if (e.key === 'Enter') {
        setTimeout(() => {
          fixTextDirection();
        }, 50);
      }
    }
    
    // Update formatting detection
    setTimeout(detectFormattingAtCursor, 50);
  }, [fixTextDirection, formatText, handleUndo, handleRedo, autoSaveDraft, detectFormattingAtCursor, saveUndoState]);

  const handleEditorPaste = useCallback((e) => {
    e.preventDefault();
    
    // Try to get HTML content first to preserve formatting
    let content = (e.clipboardData || window.clipboardData).getData('text/html');
    const isHtml = !!content;
    
    // Fall back to plain text if no HTML
    if (!content) {
      content = (e.clipboardData || window.clipboardData).getData('text/plain');
      // Preserve line breaks and spaces in plain text
      content = content
        .replace(/\n\n/g, '</p><p>')  // Double line breaks become paragraphs
        .replace(/\n/g, '<br>')        // Single line breaks become <br>
        .replace(/  /g, '&nbsp;&nbsp;'); // Preserve multiple spaces
      
      // Wrap in paragraph if it doesn't start with a tag
      if (!content.startsWith('<')) {
        content = `<p>${content}</p>`;
      }
    }
    
    // Clean up the HTML if needed (remove Word/Google Docs formatting)
    if (isHtml) {
      // Create a temporary div to clean the HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = content;
      
      // Remove unwanted tags and attributes but keep basic formatting
      const allowedTags = ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'a', 'blockquote', 'pre', 'code', 'span'];
      const allElements = tempDiv.querySelectorAll('*');
      
      allElements.forEach(element => {
        // Keep only allowed tags
        if (!allowedTags.includes(element.tagName.toLowerCase())) {
          // Replace with its content
          const parent = element.parentNode;
          while (element.firstChild) {
            parent.insertBefore(element.firstChild, element);
          }
          parent.removeChild(element);
        } else {
          // Remove all attributes except href for links
          const attributes = Array.from(element.attributes);
          attributes.forEach(attr => {
            if (!(element.tagName.toLowerCase() === 'a' && attr.name === 'href')) {
              element.removeAttribute(attr.name);
            }
          });
        }
      });
      
      content = tempDiv.innerHTML;
    }
    
    // Insert the content
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      range.deleteContents();
      
      // Create a document fragment from the HTML
      const fragment = document.createRange().createContextualFragment(content);
      const lastNode = fragment.lastChild;
      
      // Insert the fragment
      range.insertNode(fragment);
      
      // Move cursor after inserted content
      if (lastNode) {
        range.setStartAfter(lastNode);
        range.setEndAfter(lastNode);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
    
    // Fix direction after paste
    setTimeout(() => {
      fixTextDirection();
      if (editorRef.current) {
        setEditorContent(editorRef.current.innerHTML);
      }
    }, 100);
  }, [fixTextDirection]);

  // Insert image into editor with simple resizable functionality
  const insertImageIntoEditor = useCallback((image) => {
    if (!editorRef.current) return;

    const imageId = `blog-img-${Date.now()}`;
    
    // Create image wrapper element
    const wrapper = document.createElement('div');
    wrapper.className = 'blog-image-wrapper';
    wrapper.contentEditable = 'false';
    wrapper.style.cssText = 'text-align: center; margin: 30px auto; clear: both; width: 100%;';
    
    // Create container with initial size
    const container = document.createElement('div');
    container.className = 'blog-image-container';
    container.id = `container-${imageId}`;
    // Set initial width based on viewport for better visibility
    const initialWidth = Math.min(editorRef.current.offsetWidth * 0.8, 700);
    container.style.cssText = `display: inline-block; position: relative; width: ${initialWidth}px; max-width: 100%; cursor: grab; transition: box-shadow 0.2s;`;
    
    // Create image
    const img = document.createElement('img');
    img.id = imageId;
    img.src = image.url;
    img.alt = image.alt || '';
    img.style.cssText = 'display: block; width: 100%; height: auto; border-radius: 8px; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);';
    
    // Add drag functionality to resize
    let isResizing = false;
    let startWidth = 0;
    let startX = 0;
    
    // Create resize handle with better visibility
    const resizeHandle = document.createElement('div');
    resizeHandle.className = 'resize-handle';
    resizeHandle.style.cssText = 'position: absolute; bottom: -5px; right: -5px; width: 24px; height: 24px; background: #3b82f6; cursor: se-resize; border-radius: 50%; opacity: 0; transition: opacity 0.2s; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.2);';
    
    // Show resize handle and shadow on hover
    container.addEventListener('mouseenter', () => {
      resizeHandle.style.opacity = '1';
      container.style.boxShadow = '0 8px 16px rgba(0, 0, 0, 0.15)';
    });
    
    container.addEventListener('mouseleave', () => {
      if (!isResizing) {
        resizeHandle.style.opacity = '0';
        container.style.boxShadow = 'none';
      }
    });
    
    // Resize functionality
    resizeHandle.addEventListener('mousedown', (e) => {
      e.preventDefault();
      e.stopPropagation();
      isResizing = true;
      startX = e.clientX;
      startWidth = container.offsetWidth;
      container.style.cursor = 'se-resize';
      
      const handleMouseMove = (moveEvent) => {
        if (!isResizing) return;
        const width = Math.max(200, Math.min(1200, startWidth + (moveEvent.clientX - startX)));
        container.style.width = width + 'px';
        // Update percentage for responsive behavior
        const percentage = Math.min(100, (width / editorRef.current.offsetWidth) * 100);
        container.style.width = percentage + '%';
      };
      
      const handleMouseUp = () => {
        isResizing = false;
        container.style.cursor = 'grab';
        resizeHandle.style.opacity = '0';
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        
        // Save content after resize
        if (editorRef.current) {
          setEditorContent(editorRef.current.innerHTML);
        }
      };
      
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    });
    
    // Add remove button
    const removeBtn = document.createElement('button');
    removeBtn.innerHTML = '×';
    removeBtn.style.cssText = 'position: absolute; top: 10px; right: 10px; background: rgba(220, 38, 38, 0.9); color: white; border: none; border-radius: 50%; width: 30px; height: 30px; cursor: pointer; opacity: 0; transition: opacity 0.2s; font-size: 20px; line-height: 1; display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 4px rgba(0,0,0,0.2);';
    removeBtn.onclick = () => wrapper.remove();
    
    // Add visual feedback on load
    img.onload = () => {
      // Adjust container size if image is smaller
      if (img.naturalWidth < initialWidth) {
        container.style.width = img.naturalWidth + 'px';
      }
      
      // Add loaded class for animation
      container.classList.add('image-loaded');
    };
    
    // Show remove button on hover (combined with existing mouseenter)
    const existingMouseEnter = container.onmouseenter;
    container.addEventListener('mouseenter', () => {
      removeBtn.style.opacity = '1';
      if (existingMouseEnter) existingMouseEnter();
    });
    
    const existingMouseLeave = container.onmouseleave;
    container.addEventListener('mouseleave', () => {
      removeBtn.style.opacity = '0';
      if (existingMouseLeave) existingMouseLeave();
    });
    
    // Assemble elements
    container.appendChild(img);
    container.appendChild(resizeHandle);
    container.appendChild(removeBtn);
    wrapper.appendChild(container);
    
    // Insert into editor
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      range.deleteContents();
      range.insertNode(wrapper);
      
      // Add a paragraph after image for continued editing
      const p = document.createElement('p');
      p.innerHTML = '<br>';
      p.contentEditable = 'true';
      wrapper.parentNode.insertBefore(p, wrapper.nextSibling);
      
      // Move cursor to the new paragraph
      const newRange = document.createRange();
      newRange.selectNodeContents(p);
      newRange.collapse(false);
      selection.removeAllRanges();
      selection.addRange(newRange);
      
      // Focus the editor
      editorRef.current.focus();
    } else {
      editorRef.current.appendChild(wrapper);
      
      // Add a paragraph after image for continued editing
      const p = document.createElement('p');
      p.innerHTML = '<br>';
      p.contentEditable = 'true';
      editorRef.current.appendChild(p);
      
      // Move cursor to the new paragraph
      const newRange = document.createRange();
      newRange.selectNodeContents(p);
      newRange.collapse(false);
      selection.removeAllRanges();
      selection.addRange(newRange);
      
      // Focus the editor
      editorRef.current.focus();
    }
    
    // Update content
    setTimeout(() => {
      if (editorRef.current) {
        setEditorContent(editorRef.current.innerHTML);
        fixTextDirection();
      }
    }, 100);
    
    // Don't show the image manager modal
    // setShowImageManager(false);  // Commented out since we're not using modal
  }, [fixTextDirection]);

  // Removed setupImageResize function as it's now integrated into insertImageIntoEditor

  // Update editor content when blogData changes
  useEffect(() => {
    if (editorRef.current && blogData.content !== editorContent) {
      editorRef.current.innerHTML = blogData.content || '';
      setEditorContent(blogData.content || '');
      fixTextDirection();
      // Initialize undo stack with initial content
      if (blogData.content) {
        setUndoStack([blogData.content]);
      }
    }
  }, [blogData.content, fixTextDirection]);

  // Continuous direction fix
  useEffect(() => {
    const interval = setInterval(() => {
      if (editorRef.current && document.activeElement === editorRef.current) {
        fixTextDirection();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [fixTextDirection]);

  // Fetch all blogs for admin
  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const response = await apiRequest(`${API_BASE_URL}/admin`, {
        method: 'GET',
      });

      if (response.ok) {
        const data = await response.json();
        setBlogs(Array.isArray(data) ? data : []);
      } else {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error fetching blogs:', error);
      setMessage({ type: 'error', text: 'Failed to fetch blogs. Please check if the backend is running.' });
      setBlogs([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch metadata (categories, authors)
  const fetchMetadata = async () => {
    try {
      const response = await apiRequest(`${API_BASE_URL}/public/categories`);
      if (response.ok) {
        const categories = await response.json();
        setCategories(categories || []);
      }
      
      const authorsResponse = await apiRequest(`${API_BASE_URL}/public/authors`);
      if (authorsResponse.ok) {
        const authors = await authorsResponse.json();
        setAuthors(authors || []);
      }
    } catch (error) {
      console.error('Error fetching metadata:', error);
    }
  };

  // Load initial data
  useEffect(() => {
    fetchBlogs();
    fetchMetadata();
  }, []);

  // Auto-dismiss only success toasts; errors stay until the user closes them.
  useEffect(() => {
    if (message.text && message.type === 'success') {
      const timer = setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // Scroll to the top whenever the editor tab opens or the editing target changes,
  // so the user sees the editor (not still stuck looking at the list below).
  useEffect(() => {
    if (activeTab === 'editor') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [activeTab, editingId]);

  // Generate slug from title (client-side to avoid API issues)
  const generateSlug = async (title) => {
    // Use client-side slug generation to avoid authentication/API issues
    return title.toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-')          // Replace spaces with hyphens
      .replace(/-+/g, '-')           // Replace multiple hyphens with single
      .replace(/^-|-$/g, '');        // Remove leading/trailing hyphens
  };

  // Clean blog content to remove unwanted elements
  const cleanContentForSave = (content) => {
    if (!content) return '';
    
    // Create a temporary div to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
    
    // Remove all buttons with text Small, Medium, Large, Full
    const buttons = tempDiv.querySelectorAll('button');
    buttons.forEach(button => {
      const text = button.textContent.trim();
      if (text === 'Small' || text === 'Medium' || text === 'Large' || text === 'Full') {
        button.remove();
      }
    });
    
    // Remove empty divs
    const divs = tempDiv.querySelectorAll('div');
    divs.forEach(div => {
      if (div.children.length === 0 && !div.textContent.trim() && !div.querySelector('img')) {
        div.remove();
      }
    });
    
    return tempDiv.innerHTML;
  };

  // Handle form submission
  const handleSubmit = async (status = blogData.status) => {
    // Validate required fields
    if (!blogData.title.trim()) {
      setMessage({ type: 'error', text: 'Title is required' });
      return;
    }

    if (!editorContent.trim() && !blogData.content.trim()) {
      setMessage({ type: 'error', text: 'Content is required' });
      return;
    }

    setSubmitting(true);
    
    try {
      const method = editingId ? 'PUT' : 'POST';
      const url = editingId ? `${API_BASE_URL}/admin/${editingId}` : `${API_BASE_URL}/admin`;

      // Generate slug if not provided
      let slug = blogData.slug;
      if (!slug.trim()) {
        slug = await generateSlug(blogData.title);
      }

      // Clean content before saving
      const cleanedContent = cleanContentForSave(editorContent || blogData.content);

      // Prepare request data
      const requestData = {
        title: blogData.title,
        slug: slug,
        excerpt: blogData.excerpt,
        content: cleanedContent,
        featuredImageUrl: blogData.featuredImageUrl,
        category: blogData.category,
        tags: blogData.tags,
        status: status,
        author: blogData.author,
        seoTitle: blogData.seoTitle,
        seoDescription: blogData.seoDescription,
        displayOrder: blogData.displayOrder || 0,
        isActive: blogData.isActive,
        readTime: blogData.readTime,
        bgColor: blogData.bgColor,
        iconName: blogData.iconName
      };

      console.log('Submitting blog data:', requestData);

      const response = await apiRequest(url, {
        method: method,
        body: JSON.stringify(requestData)
      });

      if (response.ok) {
        const result = await response.json();
        setMessage({ 
          type: 'success', 
          text: editingId ? 'Blog updated successfully!' : 'Blog created successfully!' 
        });
        resetForm();
        await fetchBlogs();
        setActiveTab('list');
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Server error:', errorData);
        setMessage({ 
          type: 'error', 
          text: errorData.message || `Failed to save blog (${response.status})` 
        });
      }
    } catch (error) {
      console.error('Error saving blog:', error);
      setMessage({ type: 'error', text: 'Network error. Please check your connection and try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this blog?')) {
      return;
    }

    try {
      const response = await apiRequest(`${API_BASE_URL}/admin/${id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Blog deleted successfully!' });
        await fetchBlogs();
      } else {
        const errorData = await response.json().catch(() => ({}));
        setMessage({ type: 'error', text: errorData.message || 'Failed to delete blog' });
      }
    } catch (error) {
      console.error('Error deleting blog:', error);
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    }
  };

  // Handle toggle status
  const handleToggleStatus = async (id) => {
    try {
      const response = await apiRequest(`${API_BASE_URL}/admin/${id}/toggle-status`, {
        method: 'PATCH',
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Status updated successfully!' });
        await fetchBlogs();
      } else {
        const errorData = await response.json().catch(() => ({}));
        setMessage({ type: 'error', text: errorData.message || 'Failed to update status' });
      }
    } catch (error) {
      console.error('Error updating status:', error);
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    }
  };

  // Handle edit
  const handleEdit = async (blog) => {
    try {
      // Fetch full blog data for editing
      const response = await apiRequest(`${API_BASE_URL}/admin/${blog.id}`);
      if (response.ok) {
        const fullBlog = await response.json();
        setBlogData({
          title: fullBlog.title || '',
          slug: fullBlog.slug || '',
          excerpt: fullBlog.excerpt || '',
          content: fullBlog.content || '',
          featuredImageUrl: fullBlog.featuredImageUrl || '',
          category: fullBlog.category || '',
          tags: fullBlog.tags || [],
          status: fullBlog.status || 'DRAFT',
          author: fullBlog.author || 'Admin User',
          seoTitle: fullBlog.seoTitle || '',
          seoDescription: fullBlog.seoDescription || '',
          bgColor: fullBlog.bgColor || '',
          iconName: fullBlog.iconName || 'FileText',
          displayOrder: fullBlog.displayOrder || 0,
          isActive: fullBlog.isActive !== undefined ? fullBlog.isActive : true,
          readTime: fullBlog.readTime || ''
        });
        
        setEditorContent(fullBlog.content || '');
        setEditingId(blog.id);
        setActiveTab('editor');
        setMessage({ type: '', text: '' });
        
        // Set editor content and fix direction
        setTimeout(() => {
          if (editorRef.current) {
            editorRef.current.innerHTML = fullBlog.content || '';
            fixTextDirection();
          }
        }, 100);
      }
    } catch (error) {
      console.error('Error fetching blog for edit:', error);
      setMessage({ type: 'error', text: 'Failed to load blog for editing' });
    }
  };

  // Reset form
  const resetForm = () => {
    setBlogData({
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      featuredImageUrl: '',
      category: '',
      tags: [],
      status: 'DRAFT',
      author: 'Admin User',
      seoTitle: '',
      seoDescription: '',
      bgColor: '',
      iconName: 'FileText',
      displayOrder: 0,
      isActive: true,
      readTime: ''
    });
    setEditorContent('');
    setEditingId(null);
    setMessage({ type: '', text: '' });
    
    // Clear editor
    if (editorRef.current) {
      editorRef.current.innerHTML = '';
    }
  };

  // Handle image upload for content
  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files);
    
    // Check if user is authenticated
    const authToken = localStorage.getItem('authToken');
    if (!authToken) {
      setMessage({ type: 'error', text: 'Please log in to upload images' });
      return;
    }
    
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('imageType', 'content');

        const response = await apiRequestMultipart(`${UPLOAD_API_URL}/image`, formData, {
          method: 'POST'
        });

        if (response.ok) {
          const result = await response.json();
          const newImage = {
            id: Date.now() + Math.random(),
            url: result.downloadUri,
            name: file.name,
            alt: file.name.split('.')[0],
            size: (file.size / 1024).toFixed(0) + ' KB'
          };
          setImages(prev => [...prev, newImage]);
          setMessage({ type: 'success', text: 'Image uploaded successfully!' });
          
          // Insert image directly into editor (no modal)
          if (!showImageManager) {
            insertImageIntoEditor(newImage);
          }
        } else {
          console.error('Failed to upload image:', file.name);
          setMessage({ type: 'error', text: 'Failed to upload image: ' + file.name });
        }
      } catch (error) {
        console.error('Error uploading image:', error);
        setMessage({ type: 'error', text: 'Error uploading image' });
      }
    }
  };

  // Handle PDF upload
  const handlePdfUpload = async (event) => {
    const files = Array.from(event.target.files);
    
    // Check if user is authenticated
    const authToken = localStorage.getItem('authToken');
    if (!authToken) {
      setMessage({ type: 'error', text: 'Please log in to upload PDFs' });
      return;
    }
    
    for (const file of files) {
      if (!file.type.includes('pdf')) {
        setMessage({ type: 'error', text: 'Please upload only PDF files' });
        continue;
      }
      
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('fileType', 'pdf');

        const response = await apiRequestMultipart(`${UPLOAD_API_URL}/pdf`, formData, {
          method: 'POST'
        });

        if (response.ok) {
          const result = await response.json();
          const newPdf = {
            id: Date.now() + Math.random(),
            url: result.downloadUri || result.pdfUrl,
            name: file.name,
            size: (file.size / (1024 * 1024)).toFixed(2) + ' MB',
            type: 'pdf',
            content: result.content || ''
          };
          setPdfs(prev => [...prev, newPdf]);
          setMessage({ type: 'success', text: 'PDF uploaded successfully!' });
          
          // Always insert PDF directly into editor (no modal)
          insertPdfIntoEditor(newPdf);
        } else {
          console.error('Failed to upload PDF:', file.name);
          let errorText = 'Unknown error';
          try {
            const errorData = await response.json();
            errorText = errorData.error || errorData.message || 'Failed to upload';
          } catch (e) {
            errorText = await response.text();
          }
          if (response.status === 401) {
            errorText = 'Please log in to upload PDFs';
          }
          setMessage({ type: 'error', text: 'Failed to upload PDF: ' + errorText });
        }
      } catch (error) {
        console.error('Error uploading PDF:', error);
        setMessage({ type: 'error', text: 'Error uploading PDF: ' + error.message });
      }
    }
  };

  // Handle featured image upload
  const handleFeaturedImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiRequestMultipart(`${UPLOAD_API_URL}/featured-image`, formData, {
        method: 'POST'
      });

      if (response.ok) {
        const result = await response.json();
        setBlogData(prev => ({
          ...prev,
          featuredImageUrl: result.downloadUri
        }));
        setMessage({ type: 'success', text: 'Featured image uploaded successfully!' });
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.error || 'Failed to upload featured image' });
      }
    } catch (error) {
      console.error('Error uploading featured image:', error);
      setMessage({ type: 'error', text: 'Error uploading featured image' });
    }
  };

  const addTag = () => {
    if (tagInput.trim() && !blogData.tags.includes(tagInput.trim())) {
      setBlogData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    setBlogData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  // Handle input changes
  const handleInputChange = (field, value) => {
    setBlogData(prev => ({
      ...prev,
      [field]: value
    }));

    // Auto-generate slug when title changes
    if (field === 'title' && !editingId) {
      generateSlug(value).then(slug => {
        setBlogData(prev => ({
          ...prev,
          slug: slug
        }));
      });
    }
  };

  // Filter blogs
  const filteredBlogs = blogs.filter(blog => {
    const matchesSearch = blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         blog.author.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || blog.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const EditorToolbar = () => (
    <div className="editor-toolbar">
      <div className="toolbar-group">
        <button 
          onClick={() => {
            console.log('Undo button clicked, stack length:', undoStack.length);
            handleUndo();
          }} 
          className="toolbar-btn" 
          type="button" 
          title={`Undo (${navigator.platform.toUpperCase().indexOf('MAC') >= 0 ? 'Cmd' : 'Ctrl'}+Z)`}
          disabled={undoStack.length <= 1}
        >
          <Undo size={16} />
        </button>
        <button 
          onClick={() => {
            console.log('Redo button clicked, stack length:', redoStack.length);
            handleRedo();
          }} 
          className="toolbar-btn" 
          type="button" 
          title={`Redo (${navigator.platform.toUpperCase().indexOf('MAC') >= 0 ? 'Cmd' : 'Ctrl'}+Shift+Z)`}
          disabled={redoStack.length === 0}
        >
          <Redo size={16} />
        </button>
      </div>

      <div className="toolbar-group">
        <select 
          className="toolbar-select"
          value={selectedFormats.fontFamily}
          onChange={(e) => formatText('fontName', e.target.value)}
        >
          <option value="">Font Family</option>
          <option value="Arial">Arial</option>
          <option value="Times New Roman">Times New Roman</option>
          <option value="Helvetica">Helvetica</option>
          <option value="Georgia">Georgia</option>
          <option value="Courier New">Courier New</option>
          <option value="Verdana">Verdana</option>
          <option value="Impact">Impact</option>
          <option value="Comic Sans MS">Comic Sans MS</option>
        </select>
        
        <select 
          className="toolbar-select"
          value={selectedFormats.fontSize}
          onChange={(e) => formatText('fontSize', e.target.value)}
        >
          <option value="">Size</option>
          <option value="1">8pt</option>
          <option value="2">10pt</option>
          <option value="3">12pt</option>
          <option value="4">14pt</option>
          <option value="5">18pt</option>
          <option value="6">24pt</option>
          <option value="7">36pt</option>
        </select>
      </div>

      <div className="toolbar-group">
        <button 
          onClick={() => formatText('bold')} 
          className={`toolbar-btn ${selectedFormats.bold ? 'active' : ''}`}
          type="button" 
          title="Bold (Ctrl+B)"
        >
          <Bold size={16} />
        </button>
        <button 
          onClick={() => formatText('italic')} 
          className={`toolbar-btn ${selectedFormats.italic ? 'active' : ''}`}
          type="button" 
          title="Italic (Ctrl+I)"
        >
          <Italic size={16} />
        </button>
        <button 
          onClick={() => formatText('underline')} 
          className={`toolbar-btn ${selectedFormats.underline ? 'active' : ''}`}
          type="button" 
          title="Underline (Ctrl+U)"
        >
          <span style={{ textDecoration: 'underline' }}>U</span>
        </button>
        <button 
          onClick={() => formatText('strikethrough')} 
          className="toolbar-btn" 
          type="button" 
          title="Strikethrough"
        >
          <Strikethrough size={16} />
        </button>
      </div>

      <div className="toolbar-group">
        <div className="color-picker-wrapper">
          <input
            ref={colorPickerRef}
            type="color"
            className="color-input"
            value={selectedFormats.textColor}
            onChange={(e) => formatText('foreColor', e.target.value)}
            title="Text Color"
          />
          <button 
            className="toolbar-btn color-btn"
            type="button"
            onClick={() => colorPickerRef.current?.click()}
            title="Text Color"
          >
            <span style={{ 
              display: 'inline-block', 
              width: '16px', 
              height: '16px', 
              backgroundColor: selectedFormats.textColor,
              border: '1px solid #ddd',
              borderRadius: '2px'
            }}>A</span>
          </button>
        </div>
        
        <div className="color-picker-wrapper">
          <input
            ref={bgColorPickerRef}
            type="color"
            className="color-input"
            value={selectedFormats.bgColor}
            onChange={(e) => formatText('hiliteColor', e.target.value)}
            title="Background Color"
          />
          <button 
            className="toolbar-btn color-btn"
            type="button"
            onClick={() => bgColorPickerRef.current?.click()}
            title="Background Color"
          >
            <span style={{ 
              display: 'inline-block', 
              width: '16px', 
              height: '16px', 
              backgroundColor: selectedFormats.bgColor,
              border: '1px solid #ddd',
              borderRadius: '2px'
            }}>🎨</span>
          </button>
        </div>
      </div>
      
      <div className="toolbar-group">
        <button onClick={() => formatText('formatBlock', 'h1')} className="toolbar-btn" type="button" title="Heading 1">
          <Heading1 size={16} />
        </button>
        <button onClick={() => formatText('formatBlock', 'h2')} className="toolbar-btn" type="button" title="Heading 2">
          <Heading2 size={16} />
        </button>
        <button onClick={() => formatText('formatBlock', 'h3')} className="toolbar-btn" type="button" title="Heading 3">
          H3
        </button>
        <button onClick={() => formatText('formatBlock', 'p')} className="toolbar-btn" type="button" title="Paragraph">
          P
        </button>
      </div>
      
      <div className="toolbar-group">
        <button onClick={() => formatText('insertUnorderedList')} className="toolbar-btn" type="button">
          <List size={16} />
        </button>
        <button onClick={() => formatText('insertOrderedList')} className="toolbar-btn" type="button">
          <span>1.</span>
        </button>
      </div>
      
      <div className="toolbar-group">
        <button 
          onClick={() => formatText('justifyLeft')} 
          className={`toolbar-btn ${selectedFormats.alignment === 'left' ? 'active' : ''}`}
          type="button"
          title="Align Left"
        >
          <AlignLeft size={16} />
        </button>
        <button 
          onClick={() => formatText('justifyCenter')} 
          className={`toolbar-btn ${selectedFormats.alignment === 'center' ? 'active' : ''}`}
          type="button"
          title="Align Center"
        >
          <AlignCenter size={16} />
        </button>
        <button 
          onClick={() => formatText('justifyRight')} 
          className={`toolbar-btn ${selectedFormats.alignment === 'right' ? 'active' : ''}`}
          type="button"
          title="Align Right"
        >
          <AlignJustify size={16} style={{ transform: 'scaleX(-1)' }} />
        </button>
        <button 
          onClick={() => formatText('justifyFull')} 
          className={`toolbar-btn ${selectedFormats.alignment === 'justify' ? 'active' : ''}`}
          type="button"
          title="Justify"
        >
          <AlignJustify size={16} />
        </button>
      </div>
      
      <div className="toolbar-group">
        <button 
          onClick={() => formatText('createLink')} 
          className="toolbar-btn" 
          type="button"
          title="Insert Link (Ctrl+K)"
        >
          <Link size={16} />
        </button>
        <button 
          onClick={() => {
            // Direct image upload without modal
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = 'image/*';
            input.multiple = true;
            input.onchange = handleImageUpload;
            input.click();
          }} 
          className="toolbar-btn" 
          type="button"
          title="Insert Image"
        >
          <Image size={16} />
        </button>
        <button 
          onClick={() => {
            // Direct PDF upload without modal
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = 'application/pdf';
            input.onchange = handlePdfUpload;
            input.click();
          }} 
          className="toolbar-btn" 
          type="button"
          title="Insert PDF Document"
        >
          <FileText size={16} />
        </button>
        <button 
          onClick={() => formatText('insertTable')} 
          className="toolbar-btn" 
          type="button"
          title="Insert Table"
        >
          <TableIcon size={16} />
        </button>
        <button 
          onClick={() => formatText('formatBlock', 'blockquote')} 
          className="toolbar-btn" 
          type="button"
          title="Quote"
        >
          <Quote size={16} />
        </button>
        <button 
          onClick={() => formatText('formatBlock', 'pre')} 
          className="toolbar-btn" 
          type="button"
          title="Code Block"
        >
          <Code size={16} />
        </button>
        <button 
          onClick={() => formatText('removeFormat')} 
          className="toolbar-btn" 
          type="button"
          title="Clear Formatting"
        >
          <Type size={16} style={{ textDecoration: 'line-through' }} />
        </button>
      </div>
      
      <div className="toolbar-group toolbar-info">
        <span className="word-count" title="Word Count">
          📝 {wordCount} words
        </span>
        <span className="char-count" title="Character Count">
          📊 {charCount} chars
        </span>
        {isAutoSaving && (
          <span className="auto-save-indicator" title="Auto-saving...">
            💾 Saving...
          </span>
        )}
        {lastSaved && (
          <span className="last-saved" title={`Last saved: ${lastSaved.toLocaleTimeString()}`}>
            ✓ Saved
          </span>
        )}
      </div>
    </div>
  );

  // Insert PDF into editor
  const insertPdfIntoEditor = useCallback((pdf) => {
    if (!editorRef.current) return;

    // If backend provides formatted content, use it
    if (pdf.content) {
      // Clean the content to ensure no unwanted elements
      const cleanContent = pdf.content
        .replace(/<input[^>]*>/gi, '') // Remove any input tags
        .replace(/<textarea[^>]*>.*?<\/textarea>/gi, '') // Remove any textarea tags
        .replace(/sjik/gi, ''); // Remove any stray text
      
      const selection = window.getSelection();
      
      // Create wrapper div with position relative for remove button
      const wrapper = document.createElement('div');
      wrapper.className = 'blog-pdf-embed';
      wrapper.style.cssText = 'margin: 20px 0; clear: both; position: relative;';
      wrapper.contentEditable = 'false';
      
      // Add the PDF content
      wrapper.innerHTML = cleanContent;
      
      // Add remove button
      const removeBtn = document.createElement('button');
      removeBtn.innerHTML = '×';
      removeBtn.style.cssText = `
        position: absolute;
        top: 10px;
        right: 10px;
        width: 30px;
        height: 30px;
        border-radius: 50%;
        background: #ef4444;
        color: white;
        border: none;
        font-size: 20px;
        cursor: pointer;
        z-index: 10;
        opacity: 0;
        transition: opacity 0.2s;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 2px 4px rgba(0,0,0,0.2);
      `;
      
      removeBtn.onclick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (confirm('Remove this PDF from the blog?')) {
          wrapper.remove();
          if (editorRef.current) {
            setEditorContent(editorRef.current.innerHTML);
          }
        }
      };
      
      // Show remove button on hover
      wrapper.addEventListener('mouseenter', () => {
        removeBtn.style.opacity = '1';
      });
      
      wrapper.addEventListener('mouseleave', () => {
        removeBtn.style.opacity = '0';
      });
      
      wrapper.appendChild(removeBtn);
      
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        range.deleteContents();
        range.insertNode(wrapper);
        
        // Add a paragraph after PDF for continued editing
        const p = document.createElement('p');
        p.innerHTML = '<br>';
        p.contentEditable = 'true';
        wrapper.parentNode.insertBefore(p, wrapper.nextSibling);
        
        // Move cursor to the new paragraph
        const newRange = document.createRange();
        newRange.selectNodeContents(p);
        newRange.collapse(false);
        selection.removeAllRanges();
        selection.addRange(newRange);
        
        // Focus the editor
        editorRef.current.focus();
      } else {
        editorRef.current.appendChild(wrapper);
        
        // Add a paragraph after PDF for continued editing
        const p = document.createElement('p');
        p.innerHTML = '<br>';
        p.contentEditable = 'true';
        editorRef.current.appendChild(p);
        
        // Move cursor to the new paragraph
        const selection = window.getSelection();
        const newRange = document.createRange();
        newRange.selectNodeContents(p);
        newRange.collapse(false);
        selection.removeAllRanges();
        selection.addRange(newRange);
        
        // Focus the editor
        editorRef.current.focus();
      }
    } else {
      // Fallback: Create PDF embed element
      const pdfWrapper = document.createElement('div');
      pdfWrapper.className = 'pdf-embed-wrapper';
      pdfWrapper.contentEditable = 'false';
      pdfWrapper.style.cssText = 'margin: 30px 0; padding: 20px; border: 1px solid #e2e8f0; border-radius: 8px; background: #f8fafc;';
      
      const pdfHeader = document.createElement('div');
      pdfHeader.style.cssText = 'display: flex; align-items: center; justify-content: space-between; margin-bottom: 15px;';
      
      const pdfInfo = document.createElement('div');
      pdfInfo.style.cssText = 'display: flex; align-items: center; gap: 10px;';
      
      const pdfIcon = document.createElement('span');
      pdfIcon.innerHTML = '📄';
      pdfIcon.style.cssText = 'font-size: 24px;';
      
      const pdfTitle = document.createElement('strong');
      pdfTitle.textContent = pdf.name;
      pdfTitle.style.cssText = 'color: #1e293b; font-size: 16px;';
      
      const pdfSize = document.createElement('span');
      pdfSize.textContent = pdf.size;
      pdfSize.style.cssText = 'color: #6b7280; font-size: 12px; margin-left: 10px;';
      
      const downloadBtn = document.createElement('a');
      downloadBtn.href = pdf.url;
      downloadBtn.target = '_blank';
      downloadBtn.rel = 'noopener noreferrer';
      downloadBtn.textContent = 'Download PDF';
      downloadBtn.style.cssText = 'padding: 8px 16px; background: #3b82f6; color: white; text-decoration: none; border-radius: 6px; font-size: 14px;';
      
      pdfInfo.appendChild(pdfIcon);
      pdfInfo.appendChild(pdfTitle);
      pdfInfo.appendChild(pdfSize);
      pdfHeader.appendChild(pdfInfo);
      pdfHeader.appendChild(downloadBtn);
      
      const iframe = document.createElement('iframe');
      iframe.src = pdf.url + '#toolbar=0';
      iframe.style.cssText = 'width: 100%; height: 600px; border: 1px solid #e2e8f0; border-radius: 6px; background: white;';
      iframe.title = pdf.name;
      
      pdfWrapper.appendChild(pdfHeader);
      pdfWrapper.appendChild(iframe);
      
      // Insert at cursor position
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        range.deleteContents();
        range.insertNode(pdfWrapper);
        
        // Add a paragraph after PDF for continued editing
        const p = document.createElement('p');
        p.innerHTML = '<br>';
        p.contentEditable = 'true';
        pdfWrapper.parentNode.insertBefore(p, pdfWrapper.nextSibling);
        
        // Move cursor to the new paragraph
        const newRange = document.createRange();
        newRange.selectNodeContents(p);
        newRange.collapse(false);
        selection.removeAllRanges();
        selection.addRange(newRange);
        
        // Focus the editor
        editorRef.current.focus();
      } else {
        editorRef.current.appendChild(pdfWrapper);
        
        // Add a paragraph after PDF for continued editing
        const p = document.createElement('p');
        p.innerHTML = '<br>';
        p.contentEditable = 'true';
        editorRef.current.appendChild(p);
        
        // Move cursor to the new paragraph
        const newRange = document.createRange();
        newRange.selectNodeContents(p);
        newRange.collapse(false);
        selection.removeAllRanges();
        selection.addRange(newRange);
        
        // Focus the editor
        editorRef.current.focus();
      }
    }
    
    // Update content
    setTimeout(() => {
      if (editorRef.current) {
        setEditorContent(editorRef.current.innerHTML);
        fixTextDirection();
      }
    }, 100);
    
    // Don't show the PDF manager modal
    // setShowPdfManager(false);  // Commented out since we're not using modal
  }, [fixTextDirection]);

  // PDF Manager Modal
  const PdfManager = () => (
    <div className="pdf-manager-overlay">
      <div className="pdf-manager">
        <div className="pdf-manager-header">
          <h3>Attach PDF Document</h3>
          <button onClick={() => setShowPdfManager(false)} className="close-btn">
            <X size={20} />
          </button>
        </div>
        
        <div className="pdf-upload-section">
          <input
            type="file"
            ref={pdfInputRef}
            onChange={handlePdfUpload}
            accept="application/pdf"
            multiple
            style={{ display: 'none' }}
          />
          <button 
            onClick={() => pdfInputRef.current?.click()}
            className="upload-btn"
            type="button"
          >
            <Upload size={16} />
            Upload PDF Files
          </button>
        </div>
        
        <div className="pdf-gallery">
          {pdfs.map(pdf => (
            <div key={pdf.id} className="pdf-card">
              <div className="pdf-icon">
                <FileIcon size={48} color="#dc2626" />
              </div>
              <div className="pdf-details">
                <p className="pdf-name" title={pdf.name}>{pdf.name}</p>
                <p className="pdf-size">{pdf.size}</p>
                <button 
                  onClick={() => insertPdfIntoEditor(pdf)}
                  className="insert-btn"
                  type="button"
                >
                  Insert PDF
                </button>
              </div>
            </div>
          ))}
        </div>
        
        {pdfs.length === 0 && (
          <div className="no-pdfs">
            <FileText size={48} color="#9ca3af" />
            <p>No PDFs uploaded yet</p>
            <p className="hint">Click "Upload PDF Files" to add documents</p>
          </div>
        )}
      </div>
    </div>
  );

  const ImageManager = () => (
    <div className="image-manager-overlay">
      <div className="image-manager">
        <div className="image-manager-header">
          <h3>Insert Image</h3>
          <button onClick={() => setShowImageManager(false)} className="close-btn">
            <X size={20} />
          </button>
        </div>
        
        <div className="image-upload-section">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImageUpload}
            accept="image/*"
            multiple
            style={{ display: 'none' }}
          />
          <button 
            onClick={() => fileInputRef.current?.click()}
            className="upload-btn"
            type="button"
          >
            <Upload size={16} />
            Upload Images
          </button>
        </div>
        
        <div className="image-gallery">
          {images.map(image => (
            <div key={image.id} className="image-card">
              <div className="image-preview">
                <img src={image.url} alt={image.alt} />
              </div>
              <div className="image-details">
                <p className="image-name" title={image.name}>{image.name}</p>
                <div className="image-meta">
                  <span className="image-size">{image.size}</span>
                  <button 
                    onClick={() => insertImageIntoEditor(image)}
                    className="insert-btn"
                    type="button"
                  >
                    Insert
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {images.length === 0 && (
          <div className="no-images">
            <Upload size={48} color="#9ca3af" />
            <p>No images uploaded yet</p>
            <p className="hint">Click "Upload Images" to add images</p>
          </div>
        )}
      </div>
    </div>
  );

  const BlogList = () => (
    <div className="blog-list">
      <div className="blog-list-header">
        <h2>All Blogs ({filteredBlogs.length})</h2>
        <div className="blog-actions">
          <div className="search-bar">
            <Search size={16} />
            <input 
              type="text" 
              placeholder="Search blogs..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select 
            className="status-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Status</option>
            <option value="PUBLISHED">Published</option>
            <option value="DRAFT">Draft</option>
            <option value="ARCHIVED">Archived</option>
          </select>
          <button 
            onClick={() => {
              resetForm();
              setActiveTab('editor');
            }}
            className="new-blog-btn"
            type="button"
          >
            <FileText size={16} />
            New Blog
          </button>
        </div>
      </div>
      
      {loading ? (
        <div className="loading">Loading blogs...</div>
      ) : (
        <div className="blog-table">
          <table>
            <thead>
              <tr>
                <th>Title</th>
                <th>Status</th>
                <th>Author</th>
                <th>Category</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredBlogs.map(blog => (
                <tr key={blog.id}>
                  <td>
                    <div>
                      <strong>{blog.title}</strong>
                      {blog.excerpt && (
                        <div className="blog-excerpt">{blog.excerpt.substring(0, 80)}...</div>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className={`status-badge ${blog.status.toLowerCase()}`}>
                      {blog.status}
                    </span>
                    {!blog.isActive && (
                      <span className="status-badge inactive">Inactive</span>
                    )}
                  </td>
                  <td>{blog.author}</td>
                  <td>{blog.category}</td>
                  <td>{new Date(blog.createdAt).toLocaleDateString()}</td>
                  <td>
                    <div className="table-actions">
                      <button 
                        className="action-btn"
                        onClick={() => handleEdit(blog)}
                        title="Edit"
                        type="button"
                      >
                        <Edit3 size={14} />
                      </button>
                      <button 
                        className="action-btn"
                        onClick={() => handleToggleStatus(blog.id)}
                        title={blog.isActive ? 'Deactivate' : 'Activate'}
                        type="button"
                      >
                        {blog.isActive ? <Eye size={14} /> : <AlertCircle size={14} />}
                      </button>
                      <button 
                        className="action-btn danger"
                        onClick={() => handleDelete(blog.id)}
                        title="Delete"
                        type="button"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {filteredBlogs.length === 0 && !loading && (
            <div className="no-blogs">
              No blogs found. {searchTerm || statusFilter ? 'Try adjusting your filters.' : 'Click "New Blog" to get started.'}
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="blog-admin">
      <style>{`
        .blog-admin {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          max-width: 1400px;
          margin: 0 auto;
          padding: 20px;
          background: #f8fafc;
          min-height: 100vh;
        }
        
        .admin-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 30px;
          padding-bottom: 20px;
          border-bottom: 1px solid #e2e8f0;
        }
        
        .admin-title {
          font-size: 28px;
          font-weight: 600;
          color: #1e293b;
        }
        
        .admin-tabs {
          display: flex;
          gap: 10px;
          margin-bottom: 30px;
        }
        
        .tab-btn {
          padding: 10px 20px;
          border: 1px solid #e2e8f0;
          background: white;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 500;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .tab-btn.active {
          background: #3b82f6;
          color: white;
          border-color: #3b82f6;
        }
        
        .tab-btn:hover {
          background: #f1f5f9;
        }
        
        .tab-btn.active:hover {
          background: #2563eb;
        }
        
        /* Fixed top-center toast — visible regardless of scroll. */
        .message {
          position: fixed;
          top: 1rem;
          left: 50%;
          transform: translateX(-50%);
          z-index: 1500;
          padding: 0.875rem 1.25rem;
          border-radius: 8px;
          font-weight: 500;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.18);
          display: flex;
          align-items: center;
          gap: 0.75rem;
          min-width: 320px;
          max-width: min(640px, calc(100vw - 2rem));
          animation: toastSlideDown 0.28s ease-out;
        }

        .message-dismiss {
          background: none;
          border: none;
          cursor: pointer;
          font-size: 1.1rem;
          line-height: 1;
          color: inherit;
          padding: 0 0.25rem;
        }

        @keyframes toastSlideDown {
          from { transform: translate(-50%, -120%); opacity: 0; }
          to   { transform: translate(-50%, 0);     opacity: 1; }
        }

        .message.success {
          background: #d1fae5;
          color: #065f46;
          border: 1px solid #a7f3d0;
        }

        .message.error {
          background: #fee2e2;
          color: #991b1b;
          border: 1px solid #fca5a5;
        }
        
        .blog-editor {
          background: white;
          border-radius: 8px;
          padding: 30px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }
        
        .editor-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 30px;
        }
        
        .editor-title {
          font-size: 24px;
          font-weight: 600;
          color: #1e293b;
        }
        
        .editor-actions {
          display: flex;
          gap: 10px;
        }
        
        .action-btn {
          padding: 8px 16px;
          border: 1px solid #e2e8f0;
          background: white;
          border-radius: 6px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          transition: all 0.2s;
        }
        
        .action-btn.primary {
          background: #3b82f6;
          color: white;
          border-color: #3b82f6;
        }
        
        .action-btn.secondary {
          background: #f1f5f9;
          color: #64748b;
        }
        
        .action-btn.danger {
          background: #fee2e2;
          color: #dc2626;
          border-color: #fecaca;
        }
        
        .action-btn:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        
        .action-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        
        .editor-form {
          display: grid;
          grid-template-columns: 2fr 1fr;
          gap: 30px;
        }
        
        .main-content {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }
        
        .form-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        
        .form-label {
          font-weight: 500;
          color: #374151;
          font-size: 14px;
        }
        
        .form-input {
          padding: 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 16px;
          transition: border-color 0.2s;
          direction: ltr;
          text-align: left;
        }
        
        .form-input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }
        
        .form-textarea {
          padding: 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 16px;
          resize: vertical;
          min-height: 100px;
          direction: ltr;
          text-align: left;
        }
        
        .form-textarea:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }
        
        .editor-toolbar {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          padding: 12px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-bottom: none;
          border-radius: 6px 6px 0 0;
          align-items: center;
        }
        
        .toolbar-group {
          display: flex;
          gap: 2px;
          padding-right: 12px;
          border-right: 1px solid #e2e8f0;
        }
        
        .toolbar-group:last-child {
          border-right: none;
        }
        
        .toolbar-btn {
          padding: 8px;
          border: none;
          background: transparent;
          border-radius: 4px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
          min-width: 32px;
          height: 32px;
        }
        
        .toolbar-btn:hover:not(:disabled) {
          background: #e2e8f0;
          transform: translateY(-1px);
        }
        
        .toolbar-btn:active:not(:disabled) {
          transform: translateY(0);
        }
        
        .toolbar-btn.active {
          background: #3b82f6;
          color: white;
        }
        
        .toolbar-btn:disabled {
          opacity: 0.3;
          cursor: not-allowed;
          background: #f3f4f6;
        }
        
        .toolbar-btn:disabled svg {
          opacity: 0.5;
        }
        
        .toolbar-select {
          padding: 6px 10px;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          background: white;
          font-size: 14px;
          cursor: pointer;
          height: 32px;
          transition: all 0.2s;
        }
        
        .toolbar-select:hover {
          border-color: #3b82f6;
          background: #f8fafc;
        }
        
        .toolbar-select:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
        }
        
        .color-picker-wrapper {
          position: relative;
          display: inline-block;
        }
        
        .color-input {
          position: absolute;
          width: 0;
          height: 0;
          opacity: 0;
          pointer-events: none;
        }
        
        .toolbar-info {
          margin-left: auto;
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 0 12px;
          font-size: 12px;
          color: #6b7280;
          border-left: 1px solid #e2e8f0;
        }
        
        .word-count, .char-count {
          display: flex;
          align-items: center;
          gap: 4px;
        }
        
        .auto-save-indicator {
          color: #f59e0b;
          animation: pulse 1.5s infinite;
        }
        
        .last-saved {
          color: #10b981;
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        
        .content-editor {
          border: 1px solid #e2e8f0;
          border-radius: 0 0 6px 6px;
          min-height: 450px;
          padding: 20px;
          font-size: 16px;
          line-height: 1.6;
          direction: ltr !important;
          text-align: left !important;
          unicode-bidi: normal !important;
          writing-mode: horizontal-tb !important;
          background: white;
          color: #1e293b;
          overflow-y: auto;
        }
        
        .content-editor:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }
        
        .content-editor * {
          direction: ltr !important;
          text-align: left !important;
          unicode-bidi: normal !important;
          writing-mode: horizontal-tb !important;
        }
        
        .content-editor p,
        .content-editor div,
        .content-editor span,
        .content-editor h1,
        .content-editor h2,
        .content-editor h3,
        .content-editor h4,
        .content-editor h5,
        .content-editor h6,
        .content-editor ul,
        .content-editor ol,
        .content-editor li,
        .content-editor blockquote,
        .content-editor pre {
          direction: ltr !important;
          text-align: left !important;
          unicode-bidi: normal !important;
        }
        
        .content-editor h1 {
          font-size: 2em;
          font-weight: bold;
          margin: 0.67em 0;
        }
        
        .content-editor h2 {
          font-size: 1.5em;
          font-weight: bold;
          margin: 0.83em 0;
        }
        
        .content-editor h3 {
          font-size: 1.17em;
          font-weight: bold;
          margin: 1em 0;
        }
        
        .content-editor blockquote {
          border-left: 4px solid #3b82f6;
          padding-left: 16px;
          margin: 16px 0;
          font-style: italic;
          color: #6b7280;
        }
        
        .content-editor pre {
          background: #f1f5f9;
          border-radius: 4px;
          padding: 12px;
          overflow-x: auto;
          font-family: 'Courier New', monospace;
          font-size: 14px;
        }
        
        .content-editor ul,
        .content-editor ol {
          padding-left: 20px;
          margin: 16px 0;
        }
        
        .content-editor li {
          margin: 4px 0;
        }
        
        .content-editor a {
          color: #3b82f6;
          text-decoration: underline;
        }
        
        .content-editor img {
          max-width: 100%;
          height: auto;
          border-radius: 8px;
        }
        
        .blog-image-wrapper {
          margin: 20px 0;
          clear: both;
        }
        
        .blog-image-container {
          transition: outline 0.2s;
        }
        
        .blog-image-container img {
          user-select: none;
          -webkit-user-drag: none;
        }
        
        .resize-handle {
          border-radius: 50%;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
        
        /* Table styles */
        .content-editor table {
          border-collapse: collapse;
          width: 100%;
          margin: 10px 0;
        }
        
        .content-editor td, .content-editor th {
          border: 1px solid #ddd;
          padding: 8px;
          min-width: 50px;
        }
        
        .content-editor tr:nth-child(even) {
          background-color: #f9fafb;
        }
        
        .content-editor th {
          background-color: #f3f4f6;
          font-weight: bold;
        }
        
        .sidebar {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }
        
        .sidebar-section {
          background: #f8fafc;
          border-radius: 8px;
          padding: 20px;
          border: 1px solid #e2e8f0;
        }
        
        .sidebar-title {
          font-size: 16px;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 15px;
        }
        
        .featured-image {
          width: 100%;
          height: 200px;
          border: 2px dashed #d1d5db;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;
          cursor: pointer;
          transition: all 0.2s;
          background-size: cover;
          background-position: center;
          position: relative;
        }
        
        .featured-image:hover {
          border-color: #3b82f6;
          background-color: #f8fafc;
        }
        
        .featured-image.has-image {
          border: none;
        }
        
        .featured-image-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;
          color: white;
          opacity: 0;
          transition: opacity 0.2s;
          border-radius: 6px;
        }
        
        .featured-image:hover .featured-image-overlay {
          opacity: 1;
        }
        
        .tag-input-container {
          display: flex;
          gap: 8px;
          margin-bottom: 10px;
        }
        
        .tag-input {
          flex: 1;
          padding: 8px;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          direction: ltr;
          text-align: left;
        }
        
        .tag-input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }
        
        .add-tag-btn {
          padding: 8px 12px;
          background: #3b82f6;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 4px;
          transition: background 0.2s;
        }
        
        .add-tag-btn:hover {
          background: #2563eb;
        }
        
        .tags-container {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }
        
        .tag {
          background: #e0e7ff;
          color: #3730a3;
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 12px;
          display: flex;
          align-items: center;
          gap: 4px;
        }
        
        .tag-remove {
          cursor: pointer;
          color: #6b7280;
          transition: color 0.2s;
        }
        
        .tag-remove:hover {
          color: #dc2626;
        }
        
        .status-select {
          padding: 8px;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          background: white;
          direction: ltr;
          text-align: left;
          width: 100%;
        }
        
        .status-select:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }
        
        .image-manager-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        
        .image-manager {
          background: white;
          border-radius: 8px;
          width: 90%;
          max-width: 900px;
          max-height: 80vh;
          overflow-y: auto;
          box-shadow: 0 20px 40px rgba(0,0,0,0.3);
        }
        
        .image-manager-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px;
          border-bottom: 1px solid #e2e8f0;
          background: #f8fafc;
        }
        
        .image-manager-header h3 {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
          color: #1e293b;
        }
        
        .close-btn {
          background: none;
          border: none;
          cursor: pointer;
          padding: 8px;
          color: #6b7280;
          border-radius: 4px;
          transition: all 0.2s;
        }
        
        .close-btn:hover {
          background: #f1f5f9;
          color: #374151;
        }
        
        .image-upload-section {
          padding: 20px;
          border-bottom: 1px solid #e2e8f0;
          background: #f8fafc;
        }
        
        .upload-btn {
          padding: 12px 20px;
          background: #3b82f6;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.2s;
        }
        
        .upload-btn:hover {
          background: #2563eb;
          transform: translateY(-1px);
        }
        
        .image-gallery {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
          gap: 16px;
          padding: 20px;
        }
        
        .image-card {
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          overflow: hidden;
          background: white;
          transition: all 0.2s;
        }
        
        .image-card:hover {
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
          transform: translateY(-2px);
        }
        
        .image-preview {
          position: relative;
          width: 100%;
          height: 150px;
          background: #f8fafc;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        }
        
        .image-preview img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        
        .image-details {
          padding: 12px;
        }
        
        .image-name {
          font-size: 13px;
          font-weight: 500;
          margin: 0 0 8px 0;
          color: #1e293b;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        
        .image-meta {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .image-size {
          font-size: 11px;
          color: #6b7280;
        }
        
        .insert-btn {
          padding: 4px 12px;
          background: #3b82f6;
          color: white;
          border: none;
          border-radius: 4px;
          font-size: 12px;
          cursor: pointer;
          transition: background 0.2s;
        }
        
        .insert-btn:hover {
          background: #2563eb;
        }
        
        .no-images {
          text-align: center;
          padding: 60px 40px;
          color: #6b7280;
        }
        
        .no-images p {
          margin: 12px 0 4px 0;
          font-size: 14px;
        }
        
        .no-images .hint {
          font-size: 12px;
          color: #9ca3af;
        }
        
        .blog-image-wrapper {
          user-select: none;
          -webkit-user-select: none;
        }
        
        .blog-image-container:hover .resize-handle {
          opacity: 0.8 !important;
        }
        
        /* Better image display in editor */
        .content-editor .blog-image-wrapper {
          margin: 30px auto;
          text-align: center;
        }
        
        .content-editor .blog-image-container {
          display: inline-block;
          position: relative;
          transition: transform 0.2s;
        }
        
        .content-editor .blog-image-container:hover {
          transform: scale(1.02);
        }
        
        .content-editor .blog-image-container img {
          width: 100%;
          height: auto;
          display: block;
        }
        
        @keyframes imageLoadFade {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        .blog-image-container.image-loaded {
          animation: imageLoadFade 0.3s ease-out;
        }
        
        /* PDF Manager Styles */
        .pdf-manager-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        
        .pdf-manager {
          background: white;
          border-radius: 8px;
          width: 90%;
          max-width: 900px;
          max-height: 80vh;
          overflow-y: auto;
          box-shadow: 0 20px 40px rgba(0,0,0,0.3);
        }
        
        .pdf-manager-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px;
          border-bottom: 1px solid #e2e8f0;
          background: #f8fafc;
        }
        
        .pdf-manager-header h3 {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
          color: #1e293b;
        }
        
        .pdf-upload-section {
          padding: 20px;
          border-bottom: 1px solid #e2e8f0;
          background: #f8fafc;
        }
        
        .pdf-gallery {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 20px;
          padding: 20px;
        }
        
        .pdf-card {
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 20px;
          background: white;
          transition: all 0.2s;
          text-align: center;
        }
        
        .pdf-card:hover {
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
          transform: translateY(-2px);
        }
        
        .pdf-icon {
          margin-bottom: 15px;
        }
        
        .pdf-details {
          text-align: center;
        }
        
        .pdf-name {
          font-size: 14px;
          font-weight: 500;
          margin: 0 0 8px 0;
          color: #1e293b;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        
        .pdf-size {
          font-size: 12px;
          color: #6b7280;
          margin: 0 0 15px 0;
        }
        
        .no-pdfs {
          text-align: center;
          padding: 60px 40px;
          color: #6b7280;
        }
        
        .no-pdfs p {
          margin: 12px 0 4px 0;
          font-size: 14px;
        }
        
        .no-pdfs .hint {
          font-size: 12px;
          color: #9ca3af;
        }
        
        /* PDF embed styles in content */
        .pdf-embed-wrapper {
          margin: 30px 0;
          user-select: none;
        }
        
        .content-editor .pdf-embed-wrapper {
          pointer-events: none;
        }
        
        .content-editor .pdf-embed-wrapper a {
          pointer-events: auto;
        }
        
        .blog-list {
          background: white;
          border-radius: 8px;
          padding: 30px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }
        
        .blog-list-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 30px;
        }
        
        .blog-list-header h2 {
          margin: 0;
          font-size: 20px;
          font-weight: 600;
          color: #1e293b;
        }
        
        .blog-actions {
          display: flex;
          gap: 15px;
          align-items: center;
        }
        
        .search-bar {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          background: white;
          min-width: 200px;
        }
        
        .search-bar input {
          border: none;
          outline: none;
          background: transparent;
          direction: ltr;
          text-align: left;
          flex: 1;
        }
        
        .filter-btn, .new-blog-btn {
          padding: 8px 16px;
          border: 1px solid #e2e8f0;
          background: white;
          border-radius: 6px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.2s;
        }
        
        .new-blog-btn {
          background: #3b82f6;
          color: white;
          border-color: #3b82f6;
        }
        
        .new-blog-btn:hover {
          background: #2563eb;
          transform: translateY(-1px);
        }
        
        .blog-table {
          overflow-x: auto;
          border-radius: 6px;
          border: 1px solid #e2e8f0;
        }
        
        .blog-table table {
          width: 100%;
          border-collapse: collapse;
        }
        
        .blog-table th,
        .blog-table td {
          padding: 12px;
          text-align: left;
          border-bottom: 1px solid #e2e8f0;
        }
        
        .blog-table th {
          font-weight: 600;
          color: #374151;
          background: #f8fafc;
        }
        
        .blog-table tbody tr:hover {
          background: #f8fafc;
        }
        
        .blog-excerpt {
          font-size: 12px;
          color: #6b7280;
          margin-top: 4px;
        }
        
        .status-badge {
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 500;
          margin-right: 4px;
        }
        
        .status-badge.published {
          background: #d1fae5;
          color: #065f46;
        }
        
        .status-badge.draft {
          background: #fef3c7;
          color: #92400e;
        }
        
        .status-badge.archived {
          background: #f3f4f6;
          color: #374151;
        }
        
        .status-badge.inactive {
          background: #fee2e2;
          color: #991b1b;
        }
        
        .table-actions {
          display: flex;
          gap: 8px;
        }
        
        .loading {
          display: flex;
          justify-content: center;
          align-items: center;
          height: 200px;
          font-size: 16px;
          color: #6b7280;
        }
        
        .no-blogs {
          text-align: center;
          padding: 40px;
          color: #6b7280;
          font-size: 16px;
        }
        
        @media (max-width: 1024px) {
          .editor-form {
            grid-template-columns: 1fr;
          }
          
          .toolbar-group {
            border-right: none;
            padding-right: 0;
          }
        }
        
        @media (max-width: 768px) {
          .admin-header {
            flex-direction: column;
            gap: 15px;
          }
          
          .blog-actions {
            flex-direction: column;
            gap: 10px;
          }
          
          .editor-actions {
            flex-wrap: wrap;
          }
          
          .admin-tabs {
            flex-wrap: wrap;
          }
          
          .image-gallery {
            grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
          }
          
          .search-bar {
            min-width: 150px;
          }
        }
        
        @media (max-width: 640px) {
          .blog-admin {
            padding: 10px;
          }
          
          .blog-editor {
            padding: 20px;
          }
          
          .image-gallery {
            grid-template-columns: 1fr;
          }
          
          .blog-actions {
            width: 100%;
          }
          
          .search-bar {
            min-width: 100%;
          }
        }
      `}</style>
      
      <div className="admin-header">
        <h1 className="admin-title">Blog Management</h1>
        <div className="editor-actions">
          <button className="action-btn" type="button">
            <Settings size={16} />
            Settings
          </button>
          <button className="action-btn primary" type="button">
            <User size={16} />
            Profile
          </button>
        </div>
      </div>
      
      {message.text && (
        <div className={`message ${message.type}`}>
          <span style={{ flex: 1 }}>{message.text}</span>
          <button
            type="button"
            className="message-dismiss"
            aria-label="Dismiss"
            onClick={() => setMessage({ type: '', text: '' })}
          >×</button>
        </div>
      )}
      
      <div className="admin-tabs">
        <button 
          className={`tab-btn ${activeTab === 'list' ? 'active' : ''}`}
          onClick={() => setActiveTab('list')}
          type="button"
        >
          <FileText size={16} />
          All Blogs
        </button>
        <button 
          className={`tab-btn ${activeTab === 'editor' ? 'active' : ''}`}
          onClick={() => setActiveTab('editor')}
          type="button"
        >
          <Edit3 size={16} />
          {editingId ? 'Edit Blog' : 'Create Blog'}
        </button>
      </div>
      
      {activeTab === 'list' ? (
        <BlogList />
      ) : (
        <div className="blog-editor">
          <div className="editor-header">
            <h2 className="editor-title">{editingId ? 'Edit Blog' : 'Create New Blog'}</h2>
            <div className="editor-actions">
              <button 
                className="action-btn secondary"
                onClick={() => {
                  resetForm();
                  setActiveTab('list');
                }}
                type="button"
              >
                <X size={16} />
                Cancel
              </button>
              <button 
                className="action-btn"
                onClick={() => handleSubmit('DRAFT')}
                disabled={submitting}
                type="button"
              >
                <Save size={16} />
                {submitting ? 'Saving...' : 'Save Draft'}
              </button>
              <button 
                className="action-btn primary"
                onClick={() => handleSubmit('PUBLISHED')}
                disabled={submitting}
                type="button"
              >
                <Globe size={16} />
                {submitting ? 'Publishing...' : 'Publish'}
              </button>
            </div>
          </div>
          
          <form onSubmit={(e) => e.preventDefault()}>
            <div className="editor-form">
              <div className="main-content">
                <div className="form-group">
                  <label className="form-label">Blog Title *</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Enter blog title..."
                    value={blogData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">URL Slug</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="blog-url-slug"
                    value={blogData.slug}
                    onChange={(e) => handleInputChange('slug', e.target.value)}
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Excerpt</label>
                  <textarea
                    className="form-textarea"
                    placeholder="Brief description of the blog..."
                    value={blogData.excerpt}
                    onChange={(e) => handleInputChange('excerpt', e.target.value)}
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">
                    Content * 
                    <span style={{ 
                      fontSize: '12px', 
                      color: '#6b7280',
                      marginLeft: '10px',
                      fontWeight: 'normal'
                    }}>
                      ({navigator.platform.toUpperCase().indexOf('MAC') >= 0 ? 'Cmd' : 'Ctrl'}+B: Bold | 
                       {navigator.platform.toUpperCase().indexOf('MAC') >= 0 ? 'Cmd' : 'Ctrl'}+I: Italic | 
                       {navigator.platform.toUpperCase().indexOf('MAC') >= 0 ? 'Cmd' : 'Ctrl'}+U: Underline | 
                       {navigator.platform.toUpperCase().indexOf('MAC') >= 0 ? 'Cmd' : 'Ctrl'}+Z: Undo | 
                       {navigator.platform.toUpperCase().indexOf('MAC') >= 0 ? 'Cmd' : 'Ctrl'}+S: Save)
                    </span>
                  </label>
                  <EditorToolbar />
                  <div
                    ref={editorRef}
                    className="content-editor"
                    contentEditable
                    suppressContentEditableWarning={true}
                    onInput={handleEditorInput}
                    onFocus={handleEditorFocus}
                    onKeyDown={handleEditorKeyDown}
                    onPaste={handleEditorPaste}
                    onMouseUp={detectFormattingAtCursor}
                    onKeyUp={detectFormattingAtCursor}
                    dir="ltr"
                    style={{
                      direction: 'ltr',
                      textAlign: 'left',
                      unicodeBidi: 'normal',
                      writingMode: 'horizontal-tb'
                    }}
                  />
                </div>
              </div>
              
              <div className="sidebar">
                <div className="sidebar-section">
                  <h3 className="sidebar-title">Publish Settings</h3>
                  <div className="form-group">
                    <label className="form-label">Status</label>
                    <select 
                      className="status-select"
                      value={blogData.status}
                      onChange={(e) => handleInputChange('status', e.target.value)}
                    >
                      <option value="DRAFT">Draft</option>
                      <option value="PUBLISHED">Published</option>
                      <option value="ARCHIVED">Archived</option>
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">Author</label>
                    <input
                      type="text"
                      className="form-input"
                      value={blogData.author}
                      onChange={(e) => handleInputChange('author', e.target.value)}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">Display Order</label>
                    <input
                      type="number"
                      className="form-input"
                      value={blogData.displayOrder}
                      onChange={(e) => handleInputChange('displayOrder', parseInt(e.target.value) || 0)}
                      min="0"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">
                      <input
                        type="checkbox"
                        checked={blogData.isActive}
                        onChange={(e) => handleInputChange('isActive', e.target.checked)}
                        style={{ marginRight: '8px' }}
                      />
                      Active
                    </label>
                  </div>
                </div>
                
                <div className="sidebar-section">
                  <h3 className="sidebar-title">Featured Image</h3>
                  <input
                    type="file"
                    ref={featuredImageRef}
                    onChange={handleFeaturedImageUpload}
                    accept="image/*"
                    style={{ display: 'none' }}
                  />
                  <div 
                    className={`featured-image ${blogData.featuredImageUrl ? 'has-image' : ''}`}
                    onClick={() => featuredImageRef.current?.click()}
                    style={blogData.featuredImageUrl ? { backgroundImage: `url(${blogData.featuredImageUrl})` } : {}}
                  >
                    {!blogData.featuredImageUrl ? (
                      <>
                        <Upload size={24} color="#6b7280" />
                        <p>Click to upload featured image</p>
                      </>
                    ) : (
                      <div className="featured-image-overlay">
                        <Upload size={24} />
                        <p>Change Image</p>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="sidebar-section">
                  <h3 className="sidebar-title">Categories & Tags</h3>
                  <div className="form-group">
                    <label className="form-label">Category</label>
                    <select 
                      className="status-select"
                      value={blogData.category}
                      onChange={(e) => handleInputChange('category', e.target.value)}
                    >
                      <option value="">Select Category</option>
                      <option value="FUNDAMENTALS">Fundamentals</option>
                      <option value="STRATEGY">Strategy</option>
                      <option value="TAX PLANNING">Tax Planning</option>
                      <option value="EQUITY FUNDS">Equity Funds</option>
                      <option value="DEBT FUNDS">Debt Funds</option>
                      <option value="FINANCIAL PLANNING">Financial Planning</option>
                      {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">Tags</label>
                    <div className="tag-input-container">
                      <input
                        type="text"
                        className="tag-input"
                        placeholder="Add tag..."
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                      />
                      <button className="add-tag-btn" onClick={addTag} type="button">
                        <Tag size={14} />
                      </button>
                    </div>
                    <div className="tags-container">
                      {blogData.tags.map(tag => (
                        <span key={tag} className="tag">
                          {tag}
                          <span 
                            className="tag-remove" 
                            onClick={() => removeTag(tag)}
                          >
                            <X size={12} />
                          </span>
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                
                <div className="sidebar-section">
                  <h3 className="sidebar-title">SEO Settings</h3>
                  <div className="form-group">
                    <label className="form-label">SEO Title</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="SEO optimized title..."
                      value={blogData.seoTitle}
                      onChange={(e) => handleInputChange('seoTitle', e.target.value)}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">Meta Description</label>
                    <textarea
                      className="form-textarea"
                      placeholder="SEO meta description..."
                      value={blogData.seoDescription}
                      onChange={(e) => handleInputChange('seoDescription', e.target.value)}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">Read Time</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="e.g., 5 MIN READ"
                      value={blogData.readTime}
                      onChange={(e) => handleInputChange('readTime', e.target.value)}
                    />
                  </div>
                </div>
                
                <div className="sidebar-section">
                  <h3 className="sidebar-title">Design Settings</h3>
                  <div className="form-group">
                    <label className="form-label">Background Color</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="e.g., linear-gradient(135deg, #1a2332 0%, #2a4158 100%)"
                      value={blogData.bgColor}
                      onChange={(e) => handleInputChange('bgColor', e.target.value)}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">Icon Name</label>
                    <select 
                      className="status-select"
                      value={blogData.iconName}
                      onChange={(e) => handleInputChange('iconName', e.target.value)}
                    >
                      <option value="FileText">FileText</option>
                      <option value="TrendingUp">TrendingUp</option>
                      <option value="BarChart3">BarChart3</option>
                      <option value="Shield">Shield</option>
                      <option value="Target">Target</option>
                      <option value="PieChart">PieChart</option>
                      <option value="DollarSign">DollarSign</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      )}
      
      {/* Image Manager Modal removed - using direct upload instead */}
      {/* PDF Manager Modal removed - using direct upload instead */}
    </div>
    
  );
};

export default BlogAdminUpload;